// Canonical shape of the People-side filter blob.
//
// Moved out of ``containers/search/people-search-hook`` so utilities
// (legacy-search-migration, composite-search-router) and components
// (people-search-filters) can import the type + initial value without
// pulling the hook's entire mutation/RTK Query graph along — and to
// break the circular dep where the hook imports
// ``migrateSearchFilters`` which in turn imports ``SearchFilters`` from
// the hook itself.
//
// Anything that touches the SearchFilters shape (chips, migration,
// composite routing, sidebar inputs) should import from this file.
// The hook re-exports both names for backward compat with already-
// authored callsites.

export interface SearchFilters {
  // Company Attributes
  industriesInclude: string[];
  industriesExclude: string[];
  companySizes: string[];
  // Job Title
  seniority: string[];
  jobTitlesInclude: string[];
  jobTitlesExclude: string[];
  jobTitleMatchType: 'contains' | 'exact' | 'similar';
  // Experience
  yearsOfExperience: string;
  // Contact Location
  contactCountries: string[];
  contactCountriesExclude: string[];
  contactRegions: string[];
  contactRegionsExclude: string[];
  contactCities: string[];
  contactCitiesExclude: string[];
  // Company Location
  companyCountries: string[];
  companyCountriesExclude: string[];
  companyRegions: string[];
  companyRegionsExclude: string[];
  companyCities: string[];
  companyCitiesExclude: string[];
  // Profile
  skills: string[];
  connectionsMin: string;
  connectionsMax: string;
  // Education
  degreeTypes: string[];
  schoolNames: string[];
  fieldsOfStudy: string[];
  // Certifications
  certificationKeywords: string[];
  // New filters
  companyNameFilter: string;
  /** How employer NAMES match — governs both ``companyNameFilter`` and the
   *  bulk-pasted ``currentCompanyNames``. Domains and LinkedIn URLs are
   *  unaffected; those are already exact. contains → ``[.]``, exact → ``=``,
   *  similar → ``(.)``. */
  companyNameMatchType: 'contains' | 'exact' | 'similar';
  firstName: string;
  lastName: string;
  linkedinUrls: string[];
  pastCompanies: string[];
  pastTitles: string[];
  yearsAtCompany: string;
  recentlyChangedJobs: boolean;
  // ── Expanded Crustdata filters (new in PRD 2026-05) ──
  //
  // High-precision company-resolver picker output. When set, these
  // URLs filter ``current_employers.company_linkedin_profile_url IN
  // [...]`` instead of substring-matching on the company name.
  // Populated by the Identify-backed picker.
  currentCompanyLinkedInUrls: string[];
  /** Filter people by their current employer's website domain. Matches
   *  ``current_employers.company_website_domain`` exactly. Distinct
   *  from currentCompanyLinkedInUrls — domains are populated on
   *  almost every Crustdata row whereas LinkedIn URLs sometimes
   *  aren't, so this is the more reliable join key when bulk-loading
   *  companies from the Companies tab. */
  currentCompanyDomains: string[];
  /** Filter people by their current employer's name — one condition per
   *  name, token-matched (``current_employers.name [.]``) and OR'd into
   *  the same group as the LinkedIn-URL/domain conditions above, so a
   *  person matches if their employer hits ANY pasted name, domain or
   *  URL. Populated by the bulk-paste modal; distinct from the scalar
   *  ``companyNameFilter`` (single substring, AND'd). */
  currentCompanyNames: string[];
  pastCompanyLinkedInUrls: string[];
  // Function (re-enabled — backend probe confirmed support).
  functionCategoriesInclude: string[];
  pastFunctionCategoriesInclude: string[];
  // Past seniority mirror.
  pastSeniority: string[];
  // Worked-at-all toggle: when 2+ past companies are selected, wrap
  // each in its own nested AND group so the user filters for people
  // who worked at ALL of them, not ANY.
  pastCompaniesRequireAll: boolean;
  // Excludes routed to ``all_employers.*`` not_in — used for the
  // "never worked at competitor" canonical query.
  neverWorkedAtCompanies: string[];
  neverHeldTitles: string[];
  // Profile signals.
  followersMin: string;
  followersMax: string;
  languages: string[];
  openToCards: string[];
  profileUpdatedAfter: string;
  // Identity-level fields previously not exposed.
  headlineContains: string;
  summaryContains: string;
  twitterHandle: string;
  /** Sales-Nav-style boolean query against current_employers.title.
   *  Example: ``(staff AND infrastructure AND engineer) OR (senior
   *  AND platform)``. Empty string = unused. Parsed by
   *  parseBooleanQuery and translated to Crustdata's group_filter
   *  shape inside buildCrustdataFilters. */
  titleBooleanQuery: string;
  /** Same boolean grammar but applied to the `headline` and
   *  `summary` columns OR'd together (so a term matches either
   *  field). Empty string = unused. */
  keywordsBooleanQuery: string;
  // Date-range filters on the current employer.
  joinedCurrentAfter: string;
  pastLeftCompanyAfter: string;
  // Post-processing exclusion list (top-level, not part of filters).
  excludeProfileUrls: string[];
  /** Exclude people whose CURRENT employer's website domain is in this
   *  list — one ``current_employers.company_website_domain not_in``
   *  condition, attached alongside the ``excludeProfileUrls`` exclude.
   *  A pure narrow: never widens the result set. */
  excludeCompanyDomains: string[];
  // ── CRM-based exclusions (FE-E — delta on CRM sync v2) ──
  //
  // Each toggle excludes, from the People search, prospects matching a
  // CRM condition. When enabled, ``usePeopleSearch`` POSTs
  // /crm/exclusion-set for the mapped condition token, then injects the
  // returned identifier set (domains / linkedin_urls / names) into the
  // Crustdata exclude inputs (``not_in`` on linkedin URL, company
  // domain, full name). The CRM whose identifiers are fetched is the
  // first connected sync CRM (Salesforce / HubSpot) — see
  // ``useConnectedSyncCrms``. These are PURE excludes: they never widen
  // the result set, only narrow it, so a stale/partial fetch degrades to
  // "fewer prospects suppressed", never to a broken search.
  excludeCrmCustomers: boolean; // condition: current_customers
  excludeCrmActiveOpportunities: boolean; // condition: active_opportunities
  excludeCrmOwnedByMe: boolean; // condition: owned_by_me
  excludeCrmOwnedByOthers: boolean; // condition: owned_by_others
  /** A specific CRM owner's crm_user_id. Maps to ``owner:<crm_user_id>``.
   *  Empty string = unused. */
  excludeCrmOwner: string;
  /** A CRM account/lifecycle status value. Maps to
   *  ``account_status:<status>``. Empty string = unused. */
  excludeCrmAccountStatus: string;
  /** Per-CRM filter clauses — the NEW model that supersedes the flat
   *  ``excludeCrm*`` fields above. One section per connected CRM (Salesforce
   *  / HubSpot); each clause is an include/exclude condition resolved against
   *  THAT crm via /crm/exclusion-set. The flat fields stay one release for
   *  back-compat (resolved against the active CRM); new UI writes here. */
  crmFilterSections: CrmFilterSections;
  // ── Cleanlist-level dedup exclusion (CP-8) ──
  //
  // Exclude people the org ALREADY has as leads — data Cleanlist owns, so
  // this lives in the generic Exclude block, NOT in the per-CRM sections.
  // When set, ``usePeopleSearch`` sends the mapped scope as the ``cleanlist``
  // identity reference, which the server resolves and excludes. A PURE
  // exclude: it only narrows, never widens.
  //   'off'  → no Cleanlist dedup (default).
  //   'any'  → exclude anyone who is a lead ANYWHERE in the org.
  //   'list' → exclude leads in the specific ``excludeInCleanlistListIds``.
  excludeInCleanlist: 'off' | 'any' | 'list';
  /** Lead-list ids the ``'list'`` scope resolves against. Ignored when
   *  ``excludeInCleanlist !== 'list'``. */
  excludeInCleanlistListIds: string[];
  // "In Instantly" exclusion. Skips people already in the org's connected
  // Instantly workspace, matched by LinkedIn URL and employer domain. Sent as
  // the ``instantly`` identity reference, never as a search filter.
  //   'off'          → no Instantly check (default).
  //   'any_campaign' → skip anyone who is a lead in any campaign.
  //   'statuses'     → skip leads with one of ``excludeInInstantlyStatuses``.
  excludeInInstantly: 'off' | 'any_campaign' | 'statuses';
  /** The lead statuses the ``'statuses'`` mode skips. Ignored otherwise. */
  excludeInInstantlyStatuses: InstantlyLeadStatus[];
  /** Also skip emails and employer domains on the Instantly blocklist. */
  excludeInInstantlyBlocklist: boolean;
  // Composite (company-filter → people) mode. When non-empty, the
  // search router targets /people-search/by-companies instead of
  // /people-search/search.
  companyFilterCompositeMode: boolean;
  // Geo-radius filter. When set emits ``{column: "region", type:
  // "geo_distance", value: {location, distance, unit}}`` — the wire
  // shape Crustdata actually accepts (live-verified). The state shape
  // here keeps the user-facing names (``center``/``radius``); the wire
  // rename happens in buildCrustdataFilters.
  geoRadius: {
    center: string;
    radius: number;
    unit: 'km' | 'mi';
  } | null;
  // Limit Results
  maxResults: number;
  /** Frontend post-processing cap: max rows per unique company in the
   *  rendered result set. Applied AFTER Crustdata returns, so saved
   *  searches and the count badge still reflect the unfiltered total —
   *  this only affects what the table displays. Empty string = no cap. */
  limitPerCompany: string;
}

/** The Instantly lead statuses the "In Instantly" statuses mode can skip
 *  (cleanse-api ``AgentPeopleCriteria.exclude_in_instantly_statuses``). */
export type InstantlyLeadStatus =
  | 'replied'
  | 'interested'
  | 'not_interested'
  | 'bounced'
  | 'unsubscribed';

/** A connected CRM that owns a filter section. */
export type CrmFilterCrm = 'salesforce' | 'hubspot';
/** Whether a clause narrows (exclude) or requires (include) matches. */
export type CrmFilterPolarity = 'include' | 'exclude';
/** One CRM filter condition added to a CRM's section. ``condition`` is the
 *  /crm/exclusion-set token (e.g. 'current_customers', 'owner:<id>',
 *  'account_status:<v>', 'last_activity_over:<days>'); ``id`` is a stable
 *  client id for list keys + removal. */
export interface CrmFilterClause {
  id: string;
  polarity: CrmFilterPolarity;
  condition: string;
}
/** Filter clauses grouped by the CRM they resolve against. */
export type CrmFilterSections = Record<CrmFilterCrm, CrmFilterClause[]>;

export const INITIAL_FILTERS: SearchFilters = {
  industriesInclude: [],
  industriesExclude: [],
  companySizes: [],
  seniority: [],
  jobTitlesInclude: [],
  jobTitlesExclude: [],
  jobTitleMatchType: 'contains',
  yearsOfExperience: '',
  contactCountries: [],
  contactCountriesExclude: [],
  contactRegions: [],
  contactRegionsExclude: [],
  contactCities: [],
  contactCitiesExclude: [],
  companyCountries: [],
  companyCountriesExclude: [],
  companyRegions: [],
  companyRegionsExclude: [],
  companyCities: [],
  companyCitiesExclude: [],
  skills: [],
  connectionsMin: '',
  connectionsMax: '',
  degreeTypes: [],
  schoolNames: [],
  fieldsOfStudy: [],
  certificationKeywords: [],
  companyNameFilter: '',
  companyNameMatchType: 'contains',
  firstName: '',
  lastName: '',
  linkedinUrls: [],
  pastCompanies: [],
  pastTitles: [],
  yearsAtCompany: '',
  recentlyChangedJobs: false,
  currentCompanyLinkedInUrls: [],
  currentCompanyDomains: [],
  currentCompanyNames: [],
  pastCompanyLinkedInUrls: [],
  functionCategoriesInclude: [],
  pastFunctionCategoriesInclude: [],
  pastSeniority: [],
  pastCompaniesRequireAll: false,
  neverWorkedAtCompanies: [],
  neverHeldTitles: [],
  followersMin: '',
  followersMax: '',
  languages: [],
  openToCards: [],
  profileUpdatedAfter: '',
  headlineContains: '',
  summaryContains: '',
  twitterHandle: '',
  titleBooleanQuery: '',
  keywordsBooleanQuery: '',
  joinedCurrentAfter: '',
  pastLeftCompanyAfter: '',
  excludeProfileUrls: [],
  excludeCompanyDomains: [],
  excludeCrmCustomers: false,
  excludeCrmActiveOpportunities: false,
  excludeCrmOwnedByMe: false,
  excludeCrmOwnedByOthers: false,
  excludeCrmOwner: '',
  excludeCrmAccountStatus: '',
  crmFilterSections: { salesforce: [], hubspot: [] },
  excludeInCleanlist: 'off',
  excludeInCleanlistListIds: [],
  excludeInInstantly: 'off',
  excludeInInstantlyStatuses: [],
  excludeInInstantlyBlocklist: false,
  companyFilterCompositeMode: false,
  geoRadius: null,
  maxResults: 100,
  limitPerCompany: '',
};
